import { MAX_PAGE_SIZE } from "@/constants/pagination";
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetchJson } from "@/lib/authenticatedFetch";
import { fetchAllPages } from "@/lib/fetchAllPages";
import type {
  InvitesListResponse,
  Organization,
  OrganizationInvite,
  OrganizationsListResponse,
} from "@/types/organization";

/**
 * Organization role type
 */
type OrganizationRole = "owner" | "developer";

/**
 * Permission flags based on organization role
 */
export interface OrganizationPermissions {
  canInvite: boolean; // Owner only
  canEditOrg: boolean; // Owner only
  canRemoveMembers: boolean; // Owner only
  canUpdateRole: boolean; // Owner only
  canDeleteOrg: boolean; // Owner only
  canWithdrawWallet: boolean; // Owner only — mirrors backend wallet:withdraw RBAC
  canExecuteWalletActions: boolean; // Owner only — mirrors backend action_proxy:execute RBAC (swap, polymarket redeem, etc.)
  canPublishWorkflow: boolean; // Owner only — mirrors backend workflow:publish RBAC
  isOwner: boolean;
  isDeveloper: boolean;
}

/**
 * Denial reason strings surfaced in tooltips / toasts when a permission is missing.
 * Keyed by the permission flag name so `useOwnerPermission("canX")` can return the paired reason.
 */
export const OWNER_PERMISSION_DENIAL_REASONS: Partial<Record<keyof OrganizationPermissions, string>> = {
  canWithdrawWallet: "Only organization owners can send from org wallets",
  canExecuteWalletActions: "Only organization owners can execute wallet actions",
  canPublishWorkflow: "Only organization owners can publish workflows",
};

/**
 * Get permissions for a member based on their role
 * @param member - The organization member object with role
 * @returns Permission flags
 */
export function getOrgPermissions(member: { role: OrganizationRole } | null | undefined): OrganizationPermissions {
  const role = member?.role;
  const isOwner = role === "owner";
  const isDeveloper = role === "developer";

  return {
    // Owner-only permissions
    canInvite: isOwner,
    canEditOrg: isOwner,
    canRemoveMembers: isOwner,
    canUpdateRole: isOwner,
    canDeleteOrg: isOwner,
    canWithdrawWallet: isOwner,
    canExecuteWalletActions: isOwner,
    canPublishWorkflow: isOwner,

    // Role flags
    isOwner,
    isDeveloper,
  };
}

/**
 * Fetch user's organizations from V2 API (pages through all results)
 * Server-side version for use in middleware (uses native fetch with token)
 */
export async function fetchUserOrganizations(accessToken: string): Promise<OrganizationsListResponse | null> {
  if (!V2_ENDPOINT) {
    console.error("@@B3OS: V2_ENDPOINT not configured");
    return null;
  }

  try {
    const allOrgs = await fetchAllPages<Organization>(async (limit, offset) => {
      const params = new URLSearchParams();
      params.append("limit", String(limit));
      params.append("offset", String(offset));

      const response = await fetch(`${V2_ENDPOINT}/v1/organizations?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch organizations: ${response.status}`);
      }

      const data: OrganizationsListResponse = await response.json();
      return { items: data.data.items, hasMore: data.data.hasMore };
    });

    // Return in the same shape as the original single-page response
    return {
      data: {
        items: allOrgs,
        limit: MAX_PAGE_SIZE,
        offset: 0,
        hasMore: false,
      },
    };
  } catch (error) {
    console.error("@@B3OS: Error fetching organizations:", error);
    return null;
  }
}

/**
 * Fetch user's organizations (client-side, pages through all results).
 * Used by `useOrganizations` as its queryFn and by LoginLanding's
 * post-login cache prime. Routes through `authenticatedFetchJson` so
 * it picks up the b3-auth cookie automatically in the browser.
 */
export async function fetchOrganizations(): Promise<Organization[]> {
  return fetchAllPages(async (limit, offset) => {
    const params = new URLSearchParams();
    params.append("limit", String(limit));
    params.append("offset", String(offset));

    const response = await authenticatedFetchJson<OrganizationsListResponse>(
      `${V2_ENDPOINT}/v1/organizations?${params.toString()}`,
      { skipOrgId: true },
    );

    return { items: response.data.items, hasMore: response.data.hasMore };
  });
}

/**
 * Fetch user's pending invites (client-side, pages through all results).
 * Used by `useOrganizations` as its invites-query queryFn and by
 * LoginLanding's zero-org redirect branch.
 */
export async function fetchInvites(): Promise<OrganizationInvite[]> {
  return fetchAllPages(async (limit, offset) => {
    const params = new URLSearchParams();
    params.append("limit", String(limit));
    params.append("offset", String(offset));

    const response = await authenticatedFetchJson<InvitesListResponse>(
      `${V2_ENDPOINT}/v1/invites?${params.toString()}`,
      { skipOrgId: true },
    );

    return { items: response.data.items, hasMore: response.data.hasMore };
  });
}

/**
 * Check if user has pending organization invites
 * Server-side version for use in middleware (uses native fetch with token)
 */
export async function fetchUserInvites(accessToken: string): Promise<OrganizationInvite[]> {
  if (!V2_ENDPOINT) {
    return [];
  }

  try {
    const response = await fetch(`${V2_ENDPOINT}/v1/invites?limit=1`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      return [];
    }

    const data: InvitesListResponse = await response.json();
    return data.data.items;
  } catch (error) {
    console.error("@@B3OS: Error fetching invites:", error);
    return [];
  }
}
